import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Quote } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { LineMask, FadeIn, SectionTag } from "../components/marketing/Reveal";
import Hero3D from "../components/marketing/Hero3D";
import { ProblemSolution, ViewerShowcase, ClinicalTeam, WhoWeServe, CaseLibraryTeaser } from "../components/marketing/HomeSections";

const HERO_IMG = "https://images.unsplash.com/photo-1588776814546-daab30f310ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxkZW50YWwlMjBjbGluaWMlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzg3NDEzNDE1fDA&ixlib=rb-4.1.0&q=85";
const TABLET_IMG = "https://images.unsplash.com/photo-1758691463203-cce9d415b2b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxkb2N0b3IlMjBsb29raW5nJTIwYXQlMjB0YWJsZXR8ZW58MHx8fHwxNzg3NDEzNDE1fDA&ixlib=rb-4.1.0&q=85";
const CIRCUIT_IMG = "https://images.pexels.com/photos/30547568/pexels-photo-30547568.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CHAPTERS = [
  { n: "01", title: "Clinical Objective First", body: "Every case begins with your diagnosis, treatment goals and clinical priorities — not an idealized digital endpoint. We plan around the outcome you actually want." },
  { n: "02", title: "Biomechanics-Aware Planning", body: "Tooth movement, staging, anchorage, attachments, IPR, occlusion and treatment limitations — evaluated before a single aligner stage is built." },
  { n: "03", title: "Doctor-Centered, Not Software-Centered", body: "You remain in clinical control. OrthoPlaniX provides planning expertise and digital execution; the final clinical decision is always yours." },
  { n: "04", title: "Platform-Independent", body: "We work across digital orthodontic workflows and aligner platforms — never forcing your practice into one proprietary ecosystem." },
  { n: "05", title: "Plan, Review, Refine", body: "Planning is collaborative. You review the proposed plan, give feedback and request modifications before approving the final setup." },
  { n: "06", title: "Consistency at Scale", body: "A repeatable, professional planning workflow — without the cost and complexity of building a large in-house planning team." },
];

// EDIT HERE: swap these placeholder partner names with real clinic/lab logos
const PARTNERS = [
  { name: "Brightline Orthodontics", cls: "font-display font-bold text-lg tracking-tight" },
  { name: "SMILE STUDIO", cls: "font-body font-extrabold text-base tracking-[0.3em]" },
  { name: "ClearPath Labs", cls: "font-display font-black text-lg italic" },
  { name: "NORDENT CLINIC", cls: "font-body font-semibold text-sm tracking-[0.4em]" },
  { name: "AlignCraft", cls: "font-display font-bold text-xl tracking-tighter" },
  { name: "Ortho&Co.", cls: "font-body font-bold text-lg" },
];

// EDIT HERE: swap these placeholder testimonials with real ones anytime
const TESTIMONIALS = [
  { quote: "The setups feel like they were planned by someone who actually treats patients. Staging is realistic, attachments make sense, and my chair time on refinements has dropped noticeably.", name: "Dr. Sarah Mitchell", role: "Orthodontist", clinic: "Brightline Orthodontics, London" },
  { quote: "As a general dentist taking on more aligner cases, having an experienced planning team behind me changed everything. I submit the scans, review the 3D plan, and stay in full clinical control.", name: "Dr. Rajiv Menon", role: "General Dentist", clinic: "Smile Studio Dental, Dubai" },
  { quote: "We route our overflow planning through OrthoPlaniX and the consistency is excellent. Clear communication, fast revisions, and plans our doctors approve on the first or second pass.", name: "Dr. Elena Vasquez", role: "Clinical Director", clinic: "ClearPath Aligner Lab, Madrid" },
];

const STEPS = [
  { n: "1", t: "Create your account", d: "Register as a dental professional and set up your clinic profile in minutes." },
  { n: "2", t: "Submit a case", d: "Patient reference, treatment objectives, software preference — plus scans, photos and X-rays." },
  { n: "3", t: "We plan the case", d: "Clinical assessment, digital strategy, staging and biomechanics — tracked live in your portal." },
  { n: "4", t: "Review and refine", d: "Comment, request revisions and collaborate directly with your planning team." },
  { n: "5", t: "Approve and treat", d: "Download the final plan and move straight into production with confidence." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <MarketingLayout>
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none" aria-hidden="true">
          <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(rgba(148,163,184,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.4) 1px, transparent 1px)", backgroundSize: "72px 72px" }} />
        </div>
        <div className="absolute inset-0 pointer-events-none hidden md:block" aria-hidden="true">
          <Hero3D />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center w-full relative">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.6 }}>
              <span className="inline-flex items-center gap-2 rounded-full border border-neon/30 bg-neon/5 px-4 py-1.5 text-xs font-semibold tracking-widest uppercase text-neon mb-8">
                B2B Clear Aligner Treatment Planning
              </span>
            </motion.div>
            <h1 className="font-display font-black tracking-tight leading-[0.95] text-[clamp(3.2rem,8.5vw,7.5rem)]">
              <LineMask delay={0.1}>You Treat.</LineMask>
              <LineMask delay={0.25} className="text-neon">We Plan</LineMask>
              <LineMask delay={0.4}>the Case.</LineMask>
            </h1>
            <FadeIn delay={0.7} y={20}>
              <p className="mt-8 max-w-xl text-base md:text-lg text-slate-400 leading-relaxed text-balance">
                Your patients are yours. Your clinical decisions are yours. OrthoPlaniX provides the specialist digital orthodontic planning behind your aligner cases — planned by specialists, reviewed clinically, delivered within 24 hours, and approved by you before production.
              </p>
            </FadeIn>
            <FadeIn delay={0.9} y={20}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/login" data-testid="hero-submit-case-btn"
                  className="group inline-flex items-center gap-2 rounded-full bg-neon px-7 py-3.5 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(0,229,255,0.45)]">
                  Submit a Case
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link to="/how-it-works" data-testid="hero-how-it-works-btn"
                  className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:border-neon hover:text-neon">
                  See How It Works
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </FadeIn>
            <FadeIn delay={1.05} y={16}>
              <div className="mt-14 grid grid-cols-3 max-w-lg gap-6 border-t border-white/10 pt-8">
                {[["3000+", "Cases planned"], ["24h", "Standard turnaround"], ["100%", "Doctor approved"]].map(([v, l]) => (
                  <div key={l}>
                    <p className="font-display text-3xl font-black text-white">{v}</p>
                    <p className="text-xs text-slate-500 mt-1 uppercase tracking-wider whitespace-nowrap">{l}</p>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>

          <div className="lg:col-span-5 relative hidden lg:block">
            <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.5, ease: [0.76, 0, 0.24, 1] }} className="relative">
              <div className="absolute -inset-4 border border-neon/20 rounded-t-[10rem] rounded-b-2xl" aria-hidden="true" />
              <motion.div style={{ y: imgY }} className="overflow-hidden rounded-t-[10rem] rounded-b-2xl">
                <motion.img src={HERO_IMG} alt="Dental professional reviewing a clear aligner case in a modern clinic" style={{ scale: imgScale }}
                  className="w-full h-[560px] object-cover" />
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8 }}
                className="absolute -bottom-8 -left-12 bg-ink-2/90 backdrop-blur-xl border border-white/10 rounded-2xl p-5 max-w-[240px] shadow-2xl">
                <p className="text-[10px] uppercase tracking-[0.25em] text-neon mb-2">Live case tracking</p>
                <p className="font-display font-bold text-white text-lg leading-snug">Every stage, visible to your team in real time.</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PARTNER LOGO STRIP */}
      <section className="py-12 lg:py-14" data-testid="partners-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <FadeIn>
            <p className="text-center text-[10px] uppercase tracking-[0.35em] text-slate-600 mb-8">Planning partner to clinics &amp; labs worldwide</p>
          </FadeIn>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 lg:gap-x-16">
            {/* EDIT HERE: swap these placeholder partner names with real clinic/lab logos */}
            {PARTNERS.map((p, i) => (
              <FadeIn key={p.name} delay={i * 0.06}>
                <span className={`select-none whitespace-nowrap text-slate-500 opacity-60 transition-all duration-500 hover:opacity-100 hover:text-slate-300 ${p.cls}`} data-testid={`partner-logo-${i}`}>
                  {p.name}
                </span>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-white/10 bg-ink-2/60 py-5" aria-hidden="true">
        <Marquee speed={30} gradient={false} className="mask-fade-x">
          {["Clinical Objective First", "Biomechanics-Aware Planning", "Doctor-Centered", "Platform-Independent", "Plan → Review → Refine", "Consistency at Scale"].map((t) => (
            <span key={t} className="mx-10 font-display text-lg font-bold tracking-[0.2em] uppercase text-slate-500">
              {t} <span className="text-neon mx-4">•</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* PROBLEM + SOLUTION */}
      <ProblemSolution />

      {/* 3D VIEWER SHOWCASE */}
      <ViewerShowcase />

      {/* CLINICAL TEAM */}
      <ClinicalTeam />

      {/* MANIFESTO */}
      <section className="py-24 lg:py-36" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionTag>The OrthoPlaniX Approach</SectionTag>
          <FadeIn>
            <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-[1.02]">
              Clinical reasoning, <span className="text-neon">combined with</span> digital execution.
            </h2>
          </FadeIn>
          <div className="mt-20 space-y-0">
            {CHAPTERS.map((c, i) => (
              <FadeIn key={c.n} delay={i * 0.05}>
                <div className="group relative grid md:grid-cols-12 gap-6 items-start border-t border-white/10 py-10 lg:py-14 transition-colors duration-500 hover:bg-white/[0.02]">
                  <span className="md:col-span-3 font-display font-black text-7xl lg:text-8xl text-neon/10 group-hover:text-neon/25 transition-colors duration-500 leading-none select-none" aria-hidden="true">
                    {c.n}
                  </span>
                  <h3 className="md:col-span-4 font-display font-bold text-2xl lg:text-3xl tracking-tight">{c.title}</h3>
                  <p className="md:col-span-5 text-slate-400 leading-relaxed text-sm md:text-base">{c.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn>
            <Link to="/approach" className="group mt-10 inline-flex items-center gap-2 text-sm font-bold text-neon" data-testid="manifesto-approach-link">
              Read the full approach <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <WhoWeServe />

      {/* HOW IT WORKS STRIP */}
      <section className="py-24 lg:py-32" data-testid="steps-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionTag>The Workflow</SectionTag>
          <FadeIn>
            <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-2xl leading-[1.02]">
              From scan to signed-off plan.
            </h2>
          </FadeIn>
          <div className="mt-16 grid md:grid-cols-5 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
            {STEPS.map((s, i) => (
              <FadeIn key={s.n} delay={i * 0.08} className="h-full">
                <div className="h-full bg-ink p-6 lg:p-7 transition-colors duration-500 hover:bg-ink-2">
                  <p className="font-display font-black text-4xl text-neon/30">{s.n}</p>
                  <p className="font-display font-bold text-lg mt-4">{s.t}</p>
                  <p className="text-xs text-slate-500 mt-3 leading-relaxed">{s.d}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn>
            <Link to="/how-it-works" className="group mt-10 inline-flex items-center gap-2 text-sm font-bold text-neon" data-testid="steps-more-link">
              Full workflow detail <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* CASE LIBRARY TEASER */}
      <CaseLibraryTeaser />

      {/* PORTAL TEASER / PARALLAX */}
      <PortalTeaser />

      {/* TESTIMONIALS */}
      <section className="py-24 lg:py-32" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionTag>What Doctors Say</SectionTag>
          <FadeIn>
            <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-2xl leading-[1.02]">
              Trusted by the doctors <span className="text-neon">who treat.</span>
            </h2>
          </FadeIn>
          <div className="mt-16 grid md:grid-cols-3 gap-4 lg:gap-6">
            {TESTIMONIALS.map((t, i) => (
              <FadeIn key={t.name} delay={i * 0.08} className="h-full">
                <figure className="group h-full flex flex-col bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-7 lg:p-8 transition-all duration-500 hover:border-neon/40 hover:bg-white/[0.07] hover:shadow-[0_0_40px_rgba(0,229,255,0.08)]" data-testid={`testimonial-card-${i}`}>
                  <Quote className="h-7 w-7 text-neon/40 mb-5 transition-colors duration-500 group-hover:text-neon" aria-hidden="true" />
                  <blockquote className="text-sm md:text-base text-slate-300 leading-relaxed flex-1">"{t.quote}"</blockquote>
                  <figcaption className="mt-7 pt-6 border-t border-white/10">
                    <p className="font-display font-bold text-white">{t.name}</p>
                    <p className="text-xs text-slate-500 mt-1">{t.role} · {t.clinic}</p>
                  </figcaption>
                </figure>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-36 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-20" aria-hidden="true">
          <img src={CIRCUIT_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/70 to-ink" />
        </div>
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center relative">
          <FadeIn>
            <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-7xl leading-[1.0]">
              Ready to plan<br /><span className="text-neon">your next case?</span>
            </h2>
          </FadeIn>
          <FadeIn delay={0.15}>
            <p className="mt-8 text-slate-400 max-w-xl mx-auto text-base md:text-lg">
              Join dentists, clinics, labs and aligner companies worldwide who trust OrthoPlaniX as their planning department.
            </p>
          </FadeIn>
          <FadeIn delay={0.3}>
            <Link to="/register" data-testid="cta-create-account-btn"
              className="group mt-10 inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_50px_rgba(0,229,255,0.5)]">
              Create your professional account
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </FadeIn>
        </div>
      </section>
    </MarketingLayout>
  );
}

function PortalTeaser() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-60, 60]);

  return (
    <section ref={ref} className="py-24 lg:py-32 bg-ink-2/40 overflow-hidden" data-testid="portal-teaser">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionTag>The Client Portal</SectionTag>
          <FadeIn>
            <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl leading-[1.02]">
              Your cases.<br />Your timeline.<br /><span className="text-neon">Your control.</span>
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <ul className="mt-10 space-y-4">
              {[
                "Secure case submission with STL, photo and X-ray upload",
                "Unique Case ID and live status tracking at every stage",
                "Direct messaging with your treatment planning team",
                "One-click revision requests and plan downloads",
                "Full case history across your clinic",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm md:text-base text-slate-300">
                  <span className="mt-1.5 h-2 w-2 rounded-full bg-neon shrink-0" aria-hidden="true" />
                  {t}
                </li>
              ))}
            </ul>
          </FadeIn>
          <FadeIn delay={0.2}>
            <Link to="/register" data-testid="portal-teaser-btn"
              className="group mt-10 inline-flex items-center gap-2 rounded-full border border-neon/50 px-7 py-3.5 text-sm font-bold text-neon transition-colors duration-300 hover:bg-neon hover:text-ink">
              Open the portal <ArrowUpRight className="h-4 w-4" />
            </Link>
          </FadeIn>
        </div>
        <motion.div style={{ y }} className="relative">
          <div className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
            <img src={TABLET_IMG} alt="Doctors reviewing a digital treatment plan on a tablet" className="w-full h-[440px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -right-4 lg:-right-8 bg-ink/90 backdrop-blur-xl border border-neon/30 rounded-xl px-5 py-4">
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Case OPX-7K2Q4M</p>
            <p className="font-display font-bold text-neon mt-1">Treatment Plan Ready</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
